/**
 * 「未套用的檔案」清單——每一列都能就地指派到某一格。
 *
 * ## 為什麼這件事重要
 *
 * 原本這份清單是純唯讀的：它誠實地說出「這個檔我看不懂」，然後就結束了。對廠商
 * 交付的那包素材沒差（檔名全部合規），但操作者自己蒐集的素材幾乎沒有一個符合
 * 規格——`IMG_2043.png`、`西子灣中文.wav`。於是整包匯入對他們而言只會產生一長串
 * 紅字，最後還是回去逐格手動掛。
 *
 * 加上一個下拉之後，這串紅字變成待辦清單：選「這是一階圖片」→ 指派 → 下一個。
 *
 * ## 判斷仍然在後端
 *
 * 這裡不猜任何東西——不看副檔名、不猜語系、不判斷格式。使用者選什麼就送什麼，
 * 格式對不對由 `assign-file` 用與資料夾套用同一份 `SLOT_FORMAT` 檢查後回錯誤。
 * 前端多做一次判斷只會出現「CMS 說可以、後端說不行」這種最難解釋的組合。
 */

import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from './Button';
import { DISPLAY_FIELDS } from './SpotDisplayFields';
import { SLOTS } from './SpotMediaFields';
import {
  assignSpotFile,
  type AssignFileTarget,
  type DisplayLocaleCode,
  type SpotFolderPlanIssue,
  type SpotPoiPlanIssue,
} from '../lib/api-client';

const ISSUE_LABEL: Record<SpotFolderPlanIssue['reason'] | SpotPoiPlanIssue['reason'], string> = {
  unmatched: '不符規格',
  format_mismatch: '格式不符',
  duplicate: '同名重複',
  too_large: '檔案過大',
  decode_failed: '讀取失敗',
  misplaced: '放錯資料夾',
  conflict: '各語系不一致',
};

const LOCALES: readonly DisplayLocaleCode[] = ['zh', 'en', 'jp'];
const LOCALE_LABEL: Record<string, string> = { zh: '中', en: '英', jp: '日' };

/** 下拉的選項；`value` 同時編碼種類與目標，避免兩個 state 要同步。 */
interface TargetOption {
  value: string;
  label: string;
  target: Omit<AssignFileTarget, 'locale'>;
}

const TARGET_OPTIONS: readonly TargetOption[] = [
  ...SLOTS.map((s) => ({
    value: `media:${s.slot}`,
    label: `${s.label}（${s.output}）`,
    target: { kind: 'media' as const, slot: s.slot },
  })),
  ...DISPLAY_FIELDS.map((f) => ({
    value: `text:${f.key}_i18n`,
    label: `${f.label}（讀檔案內容當文字）`,
    target: { kind: 'text' as const, field: `${f.key}_i18n` },
  })),
];

/** `12/zh/4_food` → `zh`；認不出就給中文，反正使用者可以改。 */
function localeOfTag(tag: string): DisplayLocaleCode {
  const found = tag.split('/').find((s) => LOCALES.includes(s as DisplayLocaleCode));
  return (found as DisplayLocaleCode | undefined) ?? 'zh';
}

interface Props {
  spotId: string;
  title: string;
  issues: ReadonlyArray<SpotFolderPlanIssue | SpotPoiPlanIssue>;
  /** 指派成功後呼叫，讓呼叫端重載景點與計畫。 */
  onAssigned: () => void;
}

export function SpotFolderIssues({ spotId, title, issues, onAssigned }: Props): JSX.Element | null {
  if (issues.length === 0) return null;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontWeight: 600, marginBottom: 3 }}>
        {title}（{issues.length}）
      </div>
      {issues.map((issue, idx) => (
        <IssueRow
          key={`${issue.tag}|${issue.filename}|${idx}`}
          spotId={spotId}
          issue={issue}
          onAssigned={onAssigned}
        />
      ))}
    </div>
  );
}

function IssueRow({
  spotId,
  issue,
  onAssigned,
}: {
  spotId: string;
  issue: SpotFolderPlanIssue | SpotPoiPlanIssue;
  onAssigned: () => void;
}): JSX.Element {
  const [option, setOption] = useState('');
  const [locale, setLocale] = useState<DisplayLocaleCode>(localeOfTag(issue.tag));
  const [assigned, setAssigned] = useState<string | null>(null);

  // 推薦點層的 issue 沒有 media_id 欄位（型別上就沒有），同名重複的則是 null
  const mediaId = 'media_id' in issue ? issue.media_id : null;

  const assign = useMutation({
    mutationFn: () => {
      const picked = TARGET_OPTIONS.find((o) => o.value === option);
      if (!picked || !mediaId) throw new Error('請先選擇要指派到哪一格');
      return assignSpotFile(spotId, mediaId, { ...picked.target, locale } as AssignFileTarget);
    },
    onSuccess: (res) => {
      setAssigned(
        res.kind === 'media'
          ? `已掛到「${labelOf(option)}」（${LOCALE_LABEL[locale]}）`
          : `已寫入「${labelOf(option)}」（${LOCALE_LABEL[locale]}）：${truncate(res.value ?? '')}`,
      );
      onAssigned();
    },
  });

  return (
    <div style={{ padding: '4px 0', borderTop: '1px solid #eef2f7' }}>
      <div style={{ color: '#6b7280' }}>
        <span style={{ color: '#b45309' }}>
          [{ISSUE_LABEL[issue.reason as keyof typeof ISSUE_LABEL] ?? issue.reason}]
        </span>{' '}
        <code style={{ wordBreak: 'break-all' }}>{issue.filename}</code> — {issue.detail}
      </div>

      {assigned ? (
        <div style={{ color: '#047857', marginTop: 2 }}>✓ {assigned}</div>
      ) : mediaId ? (
        <div
          style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 3, flexWrap: 'wrap' }}
        >
          <select
            value={option}
            onChange={(e) => setOption(e.target.value)}
            style={{ padding: 4, fontSize: 12, maxWidth: 300 }}
          >
            <option value="">指派到…</option>
            {TARGET_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <select
            value={locale}
            onChange={(e) => setLocale(e.target.value as DisplayLocaleCode)}
            style={{ padding: 4, fontSize: 12 }}
            title="預設取自檔案所在的語系資料夾"
          >
            {LOCALES.map((l) => (
              <option key={l} value={l}>
                {LOCALE_LABEL[l]}
              </option>
            ))}
          </select>
          <Button
            variant="secondary"
            onClick={() => assign.mutate()}
            disabled={option === '' || assign.isPending}
          >
            {assign.isPending ? '指派中…' : '指派'}
          </Button>
          {assign.error && (
            <span style={{ color: '#b91c1c' }}>{(assign.error as Error).message}</span>
          )}
        </div>
      ) : null}
    </div>
  );
}

function labelOf(value: string): string {
  return TARGET_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

function truncate(s: string): string {
  const oneLine = s.replace(/\n+/g, ' ⏎ ');
  return oneLine.length > 40 ? `${oneLine.slice(0, 40)}…` : oneLine;
}
